import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Search, Eye, X, MessageCircle, Trash2 } from 'lucide-react';
import { COUNTRIES_DATA, COUNTRIES } from '../../data/locations';
import { Pagination } from './Pagination';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const PAGE_SIZE = 20;

const REQUEST_STATUS_OPTIONS = ['All', 'New', 'Reviewing', 'Contacted', 'Matched', 'Archived'];
const REQUEST_STATUS_SET = ['New', 'Reviewing', 'Contacted', 'Matched', 'Archived'];
const USER_TYPE_OPTIONS = ['All', 'contractor', 'project_owner', 'organization'];
const USER_TYPE_LABELS = { contractor: 'Contractor', project_owner: 'Project Owner', organization: 'Organization' };

const STATUS_PILL_CLASS = {
  New: 'bg-blue-900/40 text-blue-300 border border-blue-500/30',
  Reviewing: 'bg-yellow-900/40 text-yellow-300 border border-yellow-500/30',
  Contacted: 'bg-purple-900/40 text-purple-300 border border-purple-500/30',
  Matched: 'bg-green-900/40 text-green-300 border border-green-500/30',
  Archived: 'bg-slate-800/60 text-slate-400 border border-slate-700',
};

const RequestsTab = () => {
  const [profRequests, setProfRequests] = useState([]);
  const [stats, setStats] = useState({ total: 0, new: 0, reviewing: 0, contacted: 0, matched: 0, archived: 0 });
  const [totalRequests, setTotalRequests] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [reqSearch, setReqSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [reqFilters, setReqFilters] = useState({ user_type: 'All', country: 'All', state: 'All', status: 'All' });
  const [selectedReqIds, setSelectedReqIds] = useState(new Set());
  const [viewRequest, setViewRequest] = useState(null);

  const fetchStats = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/professional-requests/stats`);
      setStats(res.data);
    } catch (error) {
      console.error('Stats fetch error:', error);
    }
  }, []);

  const fetchRequests = useCallback(async () => {
    try {
      const params = { limit: PAGE_SIZE, skip: (currentPage - 1) * PAGE_SIZE };
      if (reqSearch) params.search = reqSearch;
      if (reqFilters.user_type !== 'All') params.user_type = reqFilters.user_type;
      if (reqFilters.country !== 'All') params.country = reqFilters.country;
      if (reqFilters.state !== 'All') params.state = reqFilters.state;
      if (reqFilters.status !== 'All') params.status = reqFilters.status;
      const res = await axios.get(`${API_URL}/api/admin/professional-requests`, { params });
      setProfRequests(res.data.requests || []);
      setTotalRequests(res.data.total || 0);
    } catch (error) {
      console.error('Requests fetch error:', error);
    }
  }, [reqSearch, reqFilters, currentPage]);

  useEffect(() => { fetchStats(); }, [fetchStats]);
  useEffect(() => { fetchRequests(); }, [fetchRequests]);
  useEffect(() => { setCurrentPage(1); }, [reqFilters, reqSearch]);

  const handleSearch = () => {
    setReqSearch(searchInput.trim());
  };

  const refreshAll = () => {
    fetchRequests();
    fetchStats();
  };

  const handleUpdateRequest = async (uniqueId, payload) => {
    try {
      await axios.patch(`${API_URL}/api/admin/professional-requests/${uniqueId}`, payload);
      refreshAll();
      if (viewRequest && viewRequest.unique_id === uniqueId) {
        setViewRequest({ ...viewRequest, ...payload });
      }
    } catch (error) {
      console.error('Update request error:', error);
    }
  };

  const handleDeleteRequest = async (uniqueId) => {
    if (!window.confirm('Permanently delete this request?')) return;
    try {
      await axios.delete(`${API_URL}/api/admin/professional-requests/${uniqueId}`);
      setViewRequest(null);
      setSelectedReqIds(prev => {
        const next = new Set(prev);
        next.delete(uniqueId);
        return next;
      });
      refreshAll();
    } catch (error) {
      console.error('Delete request error:', error);
    }
  };

  const handleBulkRequestAction = async (action) => {
    if (selectedReqIds.size === 0) return;
    if (action === 'delete' && !window.confirm(`Permanently delete ${selectedReqIds.size} request(s)?`)) return;
    try {
      await axios.post(`${API_URL}/api/admin/professional-requests/bulk`, {
        unique_ids: Array.from(selectedReqIds),
        action,
      });
      setSelectedReqIds(new Set());
      refreshAll();
    } catch (error) {
      console.error('Bulk request error:', error);
    }
  };

  const toggleReqSelect = (uniqueId) => {
    setSelectedReqIds(prev => {
      const next = new Set(prev);
      if (next.has(uniqueId)) next.delete(uniqueId);
      else next.add(uniqueId);
      return next;
    });
  };

  const toggleReqSelectAll = () => {
    if (selectedReqIds.size === profRequests.length) {
      setSelectedReqIds(new Set());
    } else {
      setSelectedReqIds(new Set(profRequests.map(r => r.unique_id)));
    }
  };

  const formatPhone = (phone) => (phone || '').replace(/[^0-9+]/g, '').replace(/^0/, '234').replace(/^\+/, '');

  return (
    <div data-testid="requests-tab-content">
      {/* Stats Row */}
      <div className="grid grid-cols-3 md:grid-cols-6 gap-3 mb-6">
        {[
          { label: 'Total', value: stats.total, color: 'text-slate-100' },
          { label: 'New', value: stats.new, color: 'text-blue-600' },
          { label: 'Reviewing', value: stats.reviewing, color: 'text-yellow-600' },
          { label: 'Contacted', value: stats.contacted, color: 'text-purple-600' },
          { label: 'Matched', value: stats.matched, color: 'text-green-600' },
          { label: 'Archived', value: stats.archived, color: 'text-slate-400' },
        ].map(({ label, value, color }) => (
          <div key={label} className="bg-[#0E1525] border border-slate-800 p-4 rounded" data-testid={`req-stat-${label.toLowerCase()}`}>
            <div className={`text-2xl font-bold ${color}`}>{value}</div>
            <div className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold mt-1">{label}</div>
          </div>
        ))}
      </div>

      {/* Search & Filters */}
      <div className="bg-[#0E1525] border border-slate-800 p-5 mb-6 rounded space-y-4">
        <div className="flex flex-col md:flex-row gap-3">
          <input type="text" value={searchInput} onChange={(e) => setSearchInput(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
            placeholder="Search by name, email, ID, or city..." data-testid="req-search-input"
            className="flex-1 border border-slate-700 rounded h-10 px-4 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500" />
          <button onClick={handleSearch} data-testid="req-search-button"
            className="bg-blue-600 hover:bg-blue-700 text-white px-5 py-2 text-xs uppercase tracking-wider font-semibold rounded transition-colors flex items-center gap-2">
            <Search className="h-4 w-4" /> Search
          </button>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <ReqFilter label="User Type" value={reqFilters.user_type} testId="req-filter-user-type"
            onChange={(v) => setReqFilters({ ...reqFilters, user_type: v })}
            options={USER_TYPE_OPTIONS.map(t => ({ v: t, label: t === 'All' ? 'All Types' : USER_TYPE_LABELS[t] }))} />
          <ReqFilter label="Country" value={reqFilters.country} testId="req-filter-country"
            onChange={(v) => setReqFilters({ ...reqFilters, country: v, state: 'All' })}
            options={[{ v: 'All', label: 'All Countries' }, ...COUNTRIES.map(c => ({ v: c, label: c }))]} />
          <ReqFilter label="State / Region" value={reqFilters.state} testId="req-filter-state"
            disabled={reqFilters.country === 'All'}
            onChange={(v) => setReqFilters({ ...reqFilters, state: v })}
            options={[
              { v: 'All', label: reqFilters.country === 'All' ? 'Select country first' : 'All States' },
              ...(reqFilters.country !== 'All' ? (COUNTRIES_DATA[reqFilters.country] || []).map(s => ({ v: s, label: s })) : [])
            ]} />
          <ReqFilter label="Status" value={reqFilters.status} testId="req-filter-status"
            onChange={(v) => setReqFilters({ ...reqFilters, status: v })}
            options={REQUEST_STATUS_OPTIONS.map(s => ({ v: s, label: s }))} />
        </div>
      </div>

      {/* Bulk Action Bar */}
      {selectedReqIds.size > 0 && (
        <div className="bg-blue-600 text-white px-5 py-3 mb-4 rounded flex items-center justify-between" data-testid="req-bulk-action-bar">
          <span className="text-sm font-medium">{selectedReqIds.size} request{selectedReqIds.size > 1 ? 's' : ''} selected</span>
          <div className="flex items-center gap-3">
            <button onClick={() => handleBulkRequestAction('archive')} data-testid="req-bulk-archive"
              className="bg-white/20 hover:bg-white/30 text-white px-4 py-2 rounded text-xs uppercase tracking-wider font-semibold transition-colors">
              Archive
            </button>
            <button onClick={() => handleBulkRequestAction('delete')} data-testid="req-bulk-delete"
              className="flex items-center gap-2 bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded text-xs uppercase tracking-wider font-semibold transition-colors">
              <Trash2 className="h-4 w-4" /> Delete
            </button>
            <button onClick={() => setSelectedReqIds(new Set())} data-testid="req-bulk-clear"
              className="text-white/70 hover:text-white text-xs uppercase tracking-wider font-semibold transition-colors">
              Clear
            </button>
          </div>
        </div>
      )}

      {/* Select-all bar */}
      {profRequests.length > 0 && (
        <div className="flex items-center gap-3 px-1 mb-3">
          <input
            type="checkbox"
            checked={selectedReqIds.size === profRequests.length}
            onChange={toggleReqSelectAll}
            data-testid="req-select-all"
            className="cursor-pointer"
          />
          <span className="text-[11px] uppercase tracking-widest text-slate-400 font-semibold">
            {selectedReqIds.size > 0 ? `${selectedReqIds.size} selected` : `Select all (${profRequests.length})`}
          </span>
        </div>
      )}

      {/* Requests — card list */}
      {profRequests.length === 0 ? (
        <div className="bg-[#0E1525] border border-slate-800 rounded-lg py-16 text-center text-slate-400 text-sm">
          No professional requests yet
        </div>
      ) : (
        <div className="space-y-3">
          {profRequests.map((req) => {
            const selected = selectedReqIds.has(req.unique_id);
            const phoneDigits = formatPhone(req.phone);
            return (
              <div
                key={req.unique_id}
                data-testid={`req-row-${req.unique_id}`}
                className={`relative bg-[#0E1525] border rounded-lg p-5 transition-all ${
                  selected ? 'border-blue-500 shadow-[0_4px_16px_-6px_rgba(37,99,235,0.4)]' : 'border-slate-800 hover:border-slate-700'
                }`}
              >
                {/* Top row: checkbox + name + id + status + date */}
                <div className="flex items-start gap-3 mb-2">
                  <input
                    type="checkbox"
                    checked={selected}
                    onChange={() => toggleReqSelect(req.unique_id)}
                    data-testid={`req-select-${req.unique_id}`}
                    className="mt-1 cursor-pointer"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                      <h3 className="text-base font-bold text-slate-100 uppercase tracking-tight truncate">
                        {req.full_name || req.contact_name}
                      </h3>
                      {(req.country || req.state) && (
                        <span className="text-xs text-slate-500">
                          {req.country}{req.state ? `, ${req.state}` : ''}{req.city ? `, ${req.city}` : ''}
                        </span>
                      )}
                    </div>
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 mt-0.5">
                      <span className="text-[11px] font-mono text-slate-500">{req.unique_id}</span>
                      <span className={`text-[10px] font-semibold px-2 py-0.5 rounded uppercase tracking-wider ${STATUS_PILL_CLASS[req.status] || STATUS_PILL_CLASS.New}`}>
                        {req.status || 'New'}
                      </span>
                    </div>
                  </div>
                  <div className="text-right">
                    <span className="text-[11px] text-slate-400 whitespace-nowrap">
                      {req.created_at ? new Date(req.created_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : ''}
                    </span>
                  </div>
                </div>

                {/* Contact row */}
                <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mb-3 pl-7">
                  {req.phone && (
                    <a
                      href={`https://wa.me/${phoneDigits}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`req-wa-${req.unique_id}`}
                      className="inline-flex items-center gap-1.5 text-sm text-green-400 hover:text-green-300 font-semibold"
                    >
                      <MessageCircle className="h-3.5 w-3.5" />
                      {req.phone}
                    </a>
                  )}
                  {req.email && (
                    <a href={`mailto:${req.email}`} className="text-sm text-slate-300 hover:text-blue-600 break-all">
                      {req.email}
                    </a>
                  )}
                </div>

                {/* Needs (blue pills + amber "Other" pill) */}
                {((req.needs || []).length > 0 || req.other_profession) && (
                  <div className="flex flex-wrap gap-1.5 mb-3 pl-7">
                    {(req.needs || []).filter(n => n !== 'Other (specify)').map((n, j) => (
                      <span key={j} className="text-[11px] bg-blue-900/40 text-blue-300 border border-blue-500/30 px-2.5 py-1 rounded font-semibold">
                        {n}
                      </span>
                    ))}
                    {req.other_profession && (
                      <span className="text-[11px] bg-amber-900/40 text-amber-300 border border-amber-500/30 px-2.5 py-1 rounded font-semibold" title="Custom profession specified by customer">
                        Other: {req.other_profession}
                      </span>
                    )}
                  </div>
                )}

                {/* Meta row: purpose + project type + timeline */}
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1 pl-7">
                  {req.purpose && (
                    <span className="text-[11px] bg-slate-800/60 text-slate-200 border border-slate-700 px-2.5 py-1 rounded font-semibold">
                      {req.purpose === 'project' ? 'Project-based' : req.purpose === 'organization' ? 'Organization-based' : req.purpose}
                    </span>
                  )}
                  {req.project_type && (
                    <span className="text-xs text-slate-500">{req.project_type}</span>
                  )}
                  {req.timeline && (
                    <span className="text-xs text-slate-500">{req.timeline}</span>
                  )}
                  {req.user_type && (
                    <span className="text-[10px] uppercase tracking-widest text-slate-400 italic ml-auto">
                      {USER_TYPE_LABELS[req.user_type] || req.user_type}
                    </span>
                  )}
                </div>

                {/* Description */}
                {req.description && (
                  <div className="mt-3 pl-7 pr-8">
                    <div className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold mb-1">Brief</div>
                    <p className="text-sm text-slate-300 whitespace-pre-wrap">{req.description}</p>
                  </div>
                )}

                {/* Hidden View trigger (card-wide clickable area) */}
                <button
                  onClick={() => setViewRequest(req)}
                  data-testid={`req-view-${req.unique_id}`}
                  className="absolute top-4 right-4 text-slate-400 hover:text-blue-600 transition-colors"
                  title="View full details"
                >
                  <Eye className="h-4 w-4" />
                </button>
              </div>
            );
          })}
        </div>
      )}

      <Pagination currentPage={currentPage} totalItems={totalRequests} pageSize={PAGE_SIZE} onPageChange={setCurrentPage} testIdPrefix="req-page" />

      {/* Request Detail Modal */}
      {viewRequest && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center p-4 z-50" onClick={() => setViewRequest(null)}>
          <div className="bg-[#0E1525] max-w-3xl w-full max-h-[90vh] overflow-y-auto rounded-lg shadow-xl" onClick={(e) => e.stopPropagation()}>
            <div className="sticky top-0 bg-[#0E1525] border-b border-slate-800 px-6 py-4 flex items-center justify-between rounded-t-lg">
              <div>
                <h2 className="text-lg font-bold text-slate-100" data-testid="req-modal-name">{viewRequest.full_name || viewRequest.contact_name}</h2>
                <p className="text-xs text-slate-400 font-mono" data-testid="req-modal-id">{viewRequest.unique_id}</p>
              </div>
              <button onClick={() => setViewRequest(null)} data-testid="req-modal-close"
                className="text-slate-400 hover:text-slate-300 transition-colors">
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="p-6 space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold mb-0.5">Email</div>
                  <a href={`mailto:${viewRequest.email}`} className="text-sm text-blue-400 hover:text-blue-300 hover:underline break-all">{viewRequest.email}</a>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold mb-0.5">Phone</div>
                  <a href={`https://wa.me/${formatPhone(viewRequest.phone)}`} target="_blank" rel="noopener noreferrer"
                    data-testid="req-modal-whatsapp"
                    className="text-sm text-green-600 hover:underline flex items-center gap-1.5">
                    <MessageCircle className="h-3.5 w-3.5" /> {viewRequest.phone}
                  </a>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold mb-0.5">User Type</div>
                  <div className="text-sm text-slate-200">{USER_TYPE_LABELS[viewRequest.user_type] || viewRequest.user_type || '—'}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold mb-0.5">Purpose</div>
                  <div className="text-sm text-slate-200">{viewRequest.purpose || '—'}</div>
                </div>
              </div>

              <div>
                <div className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold mb-1.5">Professionals Needed</div>
                <div className="flex flex-wrap gap-1.5">
                  {(viewRequest.needs || []).filter(n => n !== 'Other (specify)').map((n, j) => (
                    <span key={j} className="text-xs bg-blue-900/40 text-blue-300 border border-blue-500/30 px-2.5 py-1 rounded font-semibold">{n}</span>
                  ))}
                  {viewRequest.other_profession && (
                    <span className="text-xs bg-amber-900/40 text-amber-300 border border-amber-500/30 px-2.5 py-1 rounded font-semibold">
                      Other: {viewRequest.other_profession}
                    </span>
                  )}
                  {(viewRequest.needs || []).length === 0 && !viewRequest.other_profession && <span className="text-xs text-slate-500">—</span>}
                </div>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {[
                  { label: 'Title / Role', value: viewRequest.title },
                  { label: 'Country', value: viewRequest.country },
                  { label: 'State / Region', value: viewRequest.state },
                  { label: 'City', value: viewRequest.city },
                  { label: 'Project Type', value: viewRequest.project_type },
                  { label: 'Urgency', value: viewRequest.urgency },
                  { label: 'Duration', value: viewRequest.duration },
                  { label: 'Timeline', value: viewRequest.timeline },
                  { label: 'Contact Method', value: viewRequest.contact_method },
                ].filter(f => f.value).map(({ label, value }) => (
                  <div key={label}>
                    <div className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold mb-0.5">{label}</div>
                    <div className="text-sm text-slate-200">{value}</div>
                  </div>
                ))}
              </div>

              {viewRequest.description && (
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold mb-0.5">Description</div>
                  <div className="text-sm text-slate-300 bg-[#0B0F1A] border border-slate-800 rounded p-3 whitespace-pre-wrap">{viewRequest.description}</div>
                </div>
              )}

              <div className="border-t border-slate-800 pt-5">
                <div className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold mb-3">Status</div>
                <div className="flex flex-wrap gap-2 mb-4">
                  {REQUEST_STATUS_SET.map(status => (
                    <button key={status} onClick={() => handleUpdateRequest(viewRequest.unique_id, { status })}
                      data-testid={`req-status-${status.toLowerCase()}`}
                      className={`px-3 py-1.5 text-xs font-semibold rounded transition-colors ${
                        (viewRequest.status || 'New') === status ? 'bg-blue-600 text-white' : 'bg-slate-800/60 text-slate-300 border border-slate-700 hover:bg-slate-700/60'
                      }`}>
                      {status}
                    </button>
                  ))}
                </div>

                <div className="mb-4">
                  <label className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold mb-1 block">Internal Notes</label>
                  <textarea value={viewRequest.admin_notes || ''}
                    onChange={(e) => setViewRequest({ ...viewRequest, admin_notes: e.target.value })}
                    rows={3} data-testid="req-admin-notes"
                    className="w-full border border-slate-700 rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500" />
                  <button onClick={() => handleUpdateRequest(viewRequest.unique_id, { admin_notes: viewRequest.admin_notes || '' })}
                    data-testid="req-save-notes"
                    className="mt-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-1.5 text-xs uppercase tracking-wider font-semibold rounded transition-colors">
                    Save Notes
                  </button>
                </div>

                <div className="border-t border-slate-800 pt-4 mt-4">
                  <button onClick={() => handleDeleteRequest(viewRequest.unique_id)} data-testid="req-delete-button"
                    className="flex items-center gap-2 text-red-500 hover:text-red-700 text-xs uppercase tracking-wider font-semibold transition-colors">
                    <Trash2 className="h-3.5 w-3.5" /> Delete Request
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const ReqFilter = ({ label, value, onChange, options, disabled, testId }) => (
  <div>
    <label className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold mb-1 block">{label}</label>
    <select value={value} onChange={(e) => onChange(e.target.value)} data-testid={testId} disabled={disabled}
      className={`w-full border border-slate-700 rounded h-9 px-3 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 ${disabled ? 'bg-slate-800/60 text-slate-500 cursor-not-allowed' : ''}`}>
      {options.map(o => <option key={o.v} value={o.v}>{o.label}</option>)}
    </select>
  </div>
);

export default RequestsTab;
